import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Phone,
  AlertTriangle,
  Heart,
  Shield,
  Users,
  Zap,
  CheckCircle,
  Clock,
  Send,
  Bell,
  FileText,
  Briefcase
} from 'lucide-react';
import { Card } from '../common/Card';
import { Button } from '../common/Button';

interface QuickAction {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  action: () => void;
  urgent?: boolean;
}

export function QuickActions() {
  const [executedActions, setExecutedActions] = useState<Set<string>>(new Set());

  const handleCall = (number: string) => {
    window.location.href = `tel:${number}`;
  };

  const handleNotifyContacts = () => {
    // This would trigger notification to emergency contacts
    alert('Emergency contacts will be notified. This feature will be fully implemented with the notification system.');
  };

  const handleSharePack = () => {
    // This would share emergency pack
    alert('Emergency pack sharing will be implemented here.');
  };

  const handleViewPlan = () => {
    // Navigate to plans
    window.location.href = '/dashboard/wellbeing/emergency/plans';
  };

  const quickActions: QuickAction[] = [
    {
      id: 'call-000',
      title: 'Call 000 (Emergency Services)',
      description: 'For immediate life-threatening emergencies. Police, Fire, or Ambulance.',
      icon: <Phone size={32} />,
      color: 'text-white',
      bgColor: 'bg-red-600 hover:bg-red-700',
      action: () => handleCall('000'),
      urgent: true
    },
    {
      id: 'call-lifeline',
      title: 'Call Lifeline',
      description: '24/7 crisis support and suicide prevention. Confidential and non-judgmental.',
      icon: <Heart size={32} />,
      color: 'text-white',
      bgColor: 'bg-pink-600 hover:bg-pink-700',
      action: () => handleCall('131114'),
      urgent: true
    },
    {
      id: 'notify-contacts',
      title: 'Notify Emergency Contacts',
      description: 'Send emergency notification to all your emergency contacts.',
      icon: <Bell size={32} />,
      color: 'text-white',
      bgColor: 'bg-orange-600 hover:bg-orange-700',
      action: handleNotifyContacts,
      urgent: true
    },
    {
      id: 'share-pack',
      title: 'Share Emergency Pack',
      description: 'Share your emergency information pack with trusted contacts.',
      icon: <Briefcase size={32} />,
      color: 'text-white',
      bgColor: 'bg-blue-600 hover:bg-blue-700',
      action: handleSharePack
    },
    {
      id: 'view-plan',
      title: 'View Emergency Plan',
      description: 'Quick access to your emergency preparedness plans.',
      icon: <FileText size={32} />,
      color: 'text-white',
      bgColor: 'bg-purple-600 hover:bg-purple-700',
      action: handleViewPlan
    },
    {
      id: 'call-beyond-blue',
      title: 'Call Beyond Blue',
      description: '24/7 support for anxiety and depression. Information and referrals.',
      icon: <Heart size={32} />,
      color: 'text-white',
      bgColor: 'bg-indigo-600 hover:bg-indigo-700',
      action: () => handleCall('1300224636')
    },
    {
      id: 'call-1800respect',
      title: 'Call 1800RESPECT',
      description: '24/7 sexual assault and domestic violence counselling service.',
      icon: <Shield size={32} />,
      color: 'text-white',
      bgColor: 'bg-rose-600 hover:bg-rose-700',
      action: () => handleCall('1800737732')
    },
    {
      id: 'call-kids-helpline',
      title: 'Call Kids Helpline',
      description: '24/7 counselling for young people aged 5-25. Free and confidential.',
      icon: <Users size={32} />,
      color: 'text-white',
      bgColor: 'bg-teal-600 hover:bg-teal-700',
      action: () => handleCall('1800551800')
    }
  ];

  const executeAction = (action: QuickAction) => {
    action.action();
    setExecutedActions(prev => new Set(prev).add(action.id));
    
    // Reset after 3 seconds
    setTimeout(() => {
      setExecutedActions(prev => {
        const newSet = new Set(prev);
        newSet.delete(action.id);
        return newSet;
      });
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center">
              <Zap className="text-white" size={32} />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Quick Emergency Actions</h1>
              <p className="text-xl text-gray-600 mt-2">One-click access to emergency services and actions</p>
            </div>
          </div>
        </motion.div>

        {/* Urgent Actions */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <AlertTriangle className="text-red-500" size={24} />
            Urgent Actions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-fr">
            {quickActions.filter(action => action.urgent).map((action, index) => (
              <motion.div
                key={action.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="h-full"
              >
                <Card className="h-full min-h-[280px] flex flex-col p-6 cursor-pointer border-2 border-red-200 hover:border-red-400 transition-all" onClick={() => executeAction(action)}>
                  <div className="flex items-start justify-between mb-4 flex-shrink-0">
                    <div className={`w-16 h-16 rounded-xl ${action.bgColor} flex items-center justify-center ${action.color}`}>
                      {action.icon}
                    </div>
                    {executedActions.has(action.id) && (
                      <CheckCircle className="text-green-500" size={24} />
                    )}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2 flex-shrink-0">{action.title}</h3>
                  <p className="text-gray-600 text-sm mb-4 flex-1 line-clamp-3">{action.description}</p>
                  <Button
                    variant="primary"
                    className={`w-full mt-auto ${action.bgColor} ${action.color} border-0 flex-shrink-0`}
                    onClick={(e) => {
                      e.stopPropagation();
                      executeAction(action);
                    }}
                  >
                    {executedActions.has(action.id) ? 'Executed' : 'Execute Action'}
                  </Button>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Other Actions */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Other Emergency Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-fr">
            {quickActions.filter(action => !action.urgent).map((action, index) => (
              <motion.div
                key={action.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="h-full"
              >
                <Card className="h-full min-h-[280px] flex flex-col p-6 cursor-pointer hover:shadow-lg transition-all" onClick={() => executeAction(action)}>
                  <div className="flex items-start justify-between mb-4 flex-shrink-0">
                    <div className={`w-16 h-16 rounded-xl ${action.bgColor} flex items-center justify-center ${action.color}`}>
                      {action.icon}
                    </div>
                    {executedActions.has(action.id) && (
                      <CheckCircle className="text-green-500" size={24} />
                    )}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2 flex-shrink-0">{action.title}</h3>
                  <p className="text-gray-600 text-sm mb-4 flex-1 line-clamp-3">{action.description}</p>
                  <Button
                    variant="primary"
                    className={`w-full mt-auto ${action.bgColor} ${action.color} border-0 flex-shrink-0`}
                    onClick={(e) => {
                      e.stopPropagation();
                      executeAction(action);
                    }}
                  >
                    {executedActions.has(action.id) ? 'Executed' : 'Execute Action'}
                  </Button>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Important Notice */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-12"
        >
          <Card className="bg-red-50 border-red-200">
            <div className="p-6">
              <div className="flex items-start gap-4">
                <AlertTriangle className="text-red-600 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="text-lg font-bold text-red-900 mb-2">Important Notice</h3>
                  <p className="text-red-800">
                    If you are in immediate danger or experiencing a life-threatening emergency, 
                    please call <strong>000</strong> immediately. These quick actions are designed 
                    to help you access support services quickly, but they are not a substitute 
                    for emergency services in critical situations.
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}

